import cn from 'classnames';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';

import { HIGH_IMPACT_PREVIEW_FRAMES } from '@features/campaigns/builder/components/FormFields/common/CreativePreviewModal/components/PreviewHighImpactWrapper/highImpactPreviewFrames.ts';
import { PreviewCreativeModalOptions } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { HighImpactCreativeType } from '@features/campaigns/shared/types/campaign.ts';
import { InfoPanel } from '@shared/components/InfoPanel/InfoPanel.tsx';
import { InfoPanelTypes } from '@shared/components/InfoPanel/InfoPanel.types.ts';
import { i18nNameSpace } from '@shared/consts/i18n.ts';

import styles from './PreviewHighImpactWrapper.module.scss';

interface PreviewHighImpactWrapperProps {
  imageUrl: string;
  formatName: string;
  highImpactCreativeType: HighImpactCreativeType;
  editablePreview?: boolean;
  options?: PreviewCreativeModalOptions;
}

// The frame PNG, safe-area brackets included, sits above the creative, which shows through its transparent slot.
export function PreviewHighImpactWrapper({
  imageUrl,
  formatName,
  highImpactCreativeType,
  editablePreview,
  options,
}: PreviewHighImpactWrapperProps) {
  const { t } = useTranslation([i18nNameSpace.CAMPAIGNS]);
  const { frameImageSrc, slotClassName, frameContainerClassName } = HIGH_IMPACT_PREVIEW_FRAMES[highImpactCreativeType];

  return (
    <div className={styles.container}>
      {editablePreview && (
        <div className={styles.contentContainer}>
          <InfoPanel type={InfoPanelTypes.INFO} headerText={t('builder.highImpactPreview.header')}>
            <ul>
              <li key={'hint01'}>{t('builder.highImpactPreview.content.hint01', { formatName })}</li>
            </ul>
          </InfoPanel>
        </div>
      )}

      <div className={cn(styles.frameContainer, frameContainerClassName)}>
        <div className={cn(styles.creativePreviewBox, slotClassName)}>
          <img src={imageUrl} alt={'high-impact-creative-preview'} className={styles.creativeImage} />
        </div>
        <img src={frameImageSrc} alt={'browser-frame'} className={styles.frameOverlay} />
      </div>

      {editablePreview && (
        <div className={styles.footer}>
          <Button
            data-testid='high-impact-preview-reject-cta-button'
            className={styles.editButton}
            icon='pi pi-trash'
            onClick={options?.rejectImageCallback}
            text
            severity='danger'
            type='button'
          >
            {t('builder.modals.deleteImage')}
          </Button>
          <Button
            data-testid='high-impact-preview-accept-cta-button'
            onClick={options?.acceptImageCallback}
            type='button'
          >
            {t('builder.modals.ok')}
          </Button>
        </div>
      )}
    </div>
  );
}
